import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Card, Spinner } from 'react-bootstrap';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';

const AdmissionPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [formData, setFormData] = useState({
    studentName: '',
    dateOfBirth: '',
    gender: '',
    address: '',
    phone: '',
    email: '',
    parentName: '',
    occupation: '',
    course: '',
    previousSchool: '',
    photo: null
  });

  // Courses list
  const courses = [
    'O Level',
    'A Level', 
    'BCA',
    'MCA',
    'ADCA',
    'DCA',
    'PGDCA',
    'BSc IT',
    'MSc IT',
    'Web Designing',
    'Graphic Designing',
    'Digital Marketing'
  ];

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
    });
    return () => unsubscribe();
  }, []);

  // Generate ROLL number
  const generateRollNumber = () => {
    const prefix = 'ROLL';
    const randomNum = Math.floor(100000 + Math.random() * 900000); // 6-digit random number
    return `${prefix}${randomNum}`;
  };

  const validatePhoto = (file) => {
    if (!file) return true;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      alert('Please upload only JPG, JPEG or PNG images');
      return false;
    }

    if (file.size > maxSize) {
      alert('Photo size should not exceed 5MB');
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'photo' && files[0]) {
      if (!validatePhoto(files[0])) {
        e.target.value = '';
        return;
      }
    }
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const uploadPhoto = async (file) => {
    if (!file) return '';
    
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('Authentication required. Please login again.');
    }
    
    const storage = getStorage();
    if (!storage) {
      throw new Error('Storage not initialized');
    }

    try {
      const fileExtension = file.name.split('.').pop();
      // Admissions folder में upload करें
      const safeFileName = `admissions/${Date.now()}-${auth.currentUser.uid}.${fileExtension}`;
      const photoRef = storageRef(storage, safeFileName);
      
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'uploadedBy': auth.currentUser.uid,
          'uploadedAt': new Date().toISOString(),
          'studentName': formData.studentName,
          'purpose': 'admission-form'
        }
      };
      
      const snapshot = await uploadBytes(photoRef, file, metadata);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      if (!downloadURL) {
        throw new Error('Failed to get download URL');
      }
      
      return downloadURL;
    } catch (error) {
      console.error('Upload error details:', error);
      if (error.code === 'storage/unauthorized') {
        throw new Error('Storage permission denied. Please contact administrator.');
      }
      throw new Error(`Upload failed: ${error.message}`);
    }
  };

  const validateForm = () => {
    if (!formData.studentName || !formData.dateOfBirth || !formData.gender ||
        !formData.address || !formData.phone || !formData.email ||
        !formData.parentName || !formData.occupation || !formData.course) {
      alert('Please fill all required fields');
      return false;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Please enter a valid email address');
      return false;
    }

    // Validate phone number (basic validation)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      alert('Please enter a valid 10-digit phone number');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      alert('Please login to submit the form');
      return;
    }

    if (!validateForm()) return;
    
    setIsLoading(true);

    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('You must be logged in to submit the form');
      }

      let photoUrl = '';
      if (formData.photo) {
        try {
          photoUrl = await uploadPhoto(formData.photo);
          console.log('Photo uploaded successfully to admissions folder:', photoUrl);
        } catch (error) {
          alert(error.message);
          setIsLoading(false);
          return;
        }
      }

      // Generate ROLL number
      const rollNumber = generateRollNumber();

      // Firestore में डेटा सबमिट करें
      const admissionData = {
        studentName: formData.studentName,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        address: formData.address,
        phone: formData.phone,
        email: formData.email,
        parentName: formData.parentName,
        occupation: formData.occupation,
        course: formData.course,
        previousSchool: formData.previousSchool || '',
        photoUrl: photoUrl,
        rollNumber: rollNumber, // ✅ ROLL number add किया
        applicationDate: new Date().toISOString(),
        status: 'Pending',
        submissionTimestamp: new Date(),
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const db = getFirestore();
      const admissionsRef = collection(db, 'admissions');
      
      // Firestore में डॉक्यूमेंट जोड़ें
      const docRef = await addDoc(admissionsRef, admissionData);
      console.log('Admission data saved to Firestore with ID:', docRef.id);
      console.log('Generated Roll Number:', rollNumber);

      alert(`Admission form submitted successfully! Your Roll Number: ${rollNumber}`);
      
      // Reset form
      setFormData({
        studentName: '',
        dateOfBirth: '',
        gender: '',
        address: '',
        phone: '',
        email: '',
        parentName: '',
        occupation: '',
        course: '',
        previousSchool: '',
        photo: null
      });

      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Form submission error:', error);
      alert(error.message || 'Error submitting form. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Add authentication check in UI
  if (!isAuthenticated) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Please login to access the admission form</h2>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '30px', color: '#1976d2', fontWeight: '700' }}>New Admission Form</h2>
      <Card style={{ borderRadius: '15px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Student Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="studentName"
                    value={formData.studentName}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date of Birth</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Gender</Form.Label>
                  <Form.Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Select Course</Form.Label>
                  <Form.Select
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Course</option>
                    {courses.map((course, index) => (
                      <option key={index} value={course}>{course}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Parent's Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Parent's Occupation</Form.Label>
                  <Form.Control
                    type="text"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Previous School/College (if any)</Form.Label>
              <Form.Control
                type="text"
                name="previousSchool"
                value={formData.previousSchool}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Student Photo</Form.Label>
              <Form.Control
                type="file"
                name="photo"
                onChange={handleChange}
                accept="image/*"
              />
              <Form.Text className="text-muted">
                Photo will be uploaded to admissions folder in Firebase Storage
              </Form.Text>
            </Form.Group>

            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                type="submit"
                disabled={isLoading}
                style={{
                  borderRadius: '8px',
                  padding: '12px',
                  fontWeight: '600',
                  fontSize: '16px'
                }}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  'Submit Admission Form'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdmissionPage;